/**
 * Authentication Gate Store
 *
 * This module provides a centralized authentication state that can be
 * checked before making API calls. It prevents unnecessary API calls
 * when the user is not authenticated, reducing server-side error spam.
 *
 * Architecture:
 * - Subscribable store pattern (similar to backend-connection-store)
 * - Integrates with SessionProvider to track auth state
 * - Can be checked synchronously by the oRPC client before making requests
 * - In demo mode, auth is always considered valid
 */

interface AuthGateState {
	isAuthenticated: boolean;
	isLoading: boolean;
	/** Timestamp of last auth state change, used to debounce rapid changes */
	lastUpdate: number;
}

type Listener = () => void;

const listeners = new Set<Listener>();

const initialState: AuthGateState = {
	// Assume not authenticated until proven otherwise
	isAuthenticated: false,
	isLoading: true,
	lastUpdate: 0,
};

let state: AuthGateState = { ...initialState };

// Debounce window to prevent rapid state changes from causing issues
const AUTH_STATE_DEBOUNCE_MS = 100;

function emit(): void {
	for (const listener of listeners) {
		listener();
	}
}

/**
 * Subscribe to auth gate state changes
 */
export function subscribeAuthGate(listener: Listener): () => void {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

/**
 * Get the current auth gate state
 */
export function getAuthGateState(): AuthGateState {
	return state;
}

/**
 * Check if the user is authenticated (for use in API calls)
 * Returns true ONLY if definitively authenticated.
 * Returns false during loading state to prevent API calls before session is confirmed.
 * This prevents the flood of 401 errors during initial app load.
 *
 */
export function isAuthenticatedForApiCalls(): boolean {
	// Block API calls while loading - we don't know if user is authenticated yet
	if (state.isLoading) {
		return false;
	}
	return state.isAuthenticated;
}

/**
 * Mark the user as authenticated
 */
export function markAuthenticated(): void {
	const now = Date.now();
	if (state.isAuthenticated && !state.isLoading) {
		return; // Already in desired state
	}

	state = {
		isAuthenticated: true,
		isLoading: false,
		lastUpdate: now,
	};
	emit();
}

/**
 * Mark the user as not authenticated
 */
export function markNotAuthenticated(): void {
	const now = Date.now();
	if (!(state.isAuthenticated || state.isLoading)) {
		return; // Already in desired state
	}

	state = {
		isAuthenticated: false,
		isLoading: false,
		lastUpdate: now,
	};
	emit();
}

/**
 * Mark auth state as loading (e.g., during initial session check)
 */
export function markAuthLoading(): void {
	if (state.isLoading) {
		return; // Already loading
	}

	state = {
		...state,
		isLoading: true,
		lastUpdate: Date.now(),
	};
	emit();
}

/**
 * Reset auth gate to initial state (for testing or logout)
 */
export function resetAuthGate(): void {
	state = { ...initialState };
	emit();
}

/**
 * Check if enough time has passed since last update to consider new changes
 * This prevents rapid flickering during auth state transitions
 */
export function canUpdateAuthState(): boolean {
	return Date.now() - state.lastUpdate > AUTH_STATE_DEBOUNCE_MS;
}

const _authGateStore = {
	subscribe: subscribeAuthGate,
	getState: getAuthGateState,
	isAuthenticatedForApiCalls,
	markAuthenticated,
	markNotAuthenticated,
	markAuthLoading,
	reset: resetAuthGate,
};
